import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { Eye, EyeOff, Loader2, AlertCircle, ArrowRight, Mail, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';

interface AuthProvider {
  type: 'email' | 'telegram' | 'google' | 'github';
  displayName: string;
  displayOrder: number;
  settings: {
    botUsername?: string;
    botId?: string;
    clientId?: string;
  };
}

interface AuthProvidersResponse {
  providers: AuthProvider[];
}

interface OAuthLoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    fullName: string | null;
    avatarUrl: string | null;
    isSuperAdmin: boolean;
  };
  isNewUser: boolean;
  expiresAt: string;
}

// Google icon SVG
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Telegram icon SVG
const TelegramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#229ED9">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export function LoginPage() {
  const navigate = useNavigate();
  const { login, setTokenAndFetchUser } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  // Auth providers state
  const [providers, setProviders] = useState<AuthProvider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  
  // Fetch enabled auth providers
  useEffect(() => {
    async function fetchProviders() {
      try {
        const response = await api.get<AuthProvidersResponse>('/auth-providers/public');
        setProviders(response.providers);
      } catch (error) {
        console.error('Failed to fetch auth providers:', error);
      } finally {
        setIsLoadingProviders(false);
      }
    }
    
    fetchProviders();
  }, []);
  
  // Load Google Sign-In script
  useEffect(() => {
    const googleProvider = providers.find(p => p.type === 'google');
    if (!googleProvider?.settings?.clientId) return;
    
    if ((window as any).google?.accounts?.id) {
      setGoogleScriptLoaded(true);
      initializeGoogle(googleProvider.settings.clientId);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setGoogleScriptLoaded(true);
      initializeGoogle(googleProvider.settings.clientId!);
    };
    document.head.appendChild(script);
    
    return () => {
      script.remove();
    };
  }, [providers]);
  
  const initializeGoogle = (clientId: string) => {
    if (!(window as any).google?.accounts?.id) return;
    
    (window as any).google.accounts.id.initialize({
      client_id: clientId,
      callback: handleGoogleCallback,
    });
  };
  
  const handleGoogleCallback = async (response: { credential: string }) => {
    setGoogleLoading(true);
    setError('');
    
    try {
      const verifyResponse = await api.post<OAuthLoginResponse>('/auth-providers/google/verify', {
        idToken: response.credential,
      });
      
      await setTokenAndFetchUser(verifyResponse.token);
      navigate('/app');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Google login failed';
      setError(message);
    } finally {
      setGoogleLoading(false);
    }
  };
  
  // Listen for Google auth callback messages from popup
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data?.type === 'google-auth-success') {
        setGoogleLoading(false);
        await setTokenAndFetchUser(event.data.token);
        navigate('/app');
      } else if (event.data?.type === 'google-auth-error') {
        setGoogleLoading(false);
        setError(event.data.error || 'Google authentication failed');
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, setTokenAndFetchUser]);
  
  const handleGoogleClick = () => {
    if (!(window as any).google?.accounts?.id) {
      setError('Google Sign-In not loaded. Please refresh the page.');
      return;
    }
    
    (window as any).google.accounts.id.prompt((notification: any) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        const googleProvider = providers.find(p => p.type === 'google');
        if (googleProvider?.settings?.clientId) {
          setGoogleLoading(true);
          
          const width = 500;
          const height = 600;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          
          const popup = window.open(
            `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleProvider.settings.clientId}&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/google/callback')}&response_type=token id_token&scope=openid email profile&nonce=${Date.now()}`,
            'google-signin',
            `width=${width},height=${height},left=${left},top=${top}`
          );
          
          // Handle popup being closed without completing auth
          if (popup) {
            const checkClosed = setInterval(() => {
              if (popup.closed) {
                clearInterval(checkClosed);
                setGoogleLoading(false);
              }
            }, 500);
          } else {
            setGoogleLoading(false);
            setError('Popup blocked. Please allow popups for this site.');
          }
        }
      }
    });
  };
  
  // Initialize Telegram callback
  useEffect(() => {
    const telegramProvider = providers.find(p => p.type === 'telegram');
    if (!telegramProvider?.settings?.botUsername) return;
    
    (window as any).onTelegramAuth = async (user: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
      auth_date: number;
      hash: string;
    }) => {
      setTelegramLoading(true);
      setError('');
      
      try {
        const response = await api.post<OAuthLoginResponse>('/auth-providers/telegram/verify', user);
        await setTokenAndFetchUser(response.token);
        navigate('/app');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Telegram login failed';
        setError(message);
      } finally {
        setTelegramLoading(false);
      }
    };
    
    return () => {
      delete (window as any).onTelegramAuth;
    };
  }, [providers, navigate, setTokenAndFetchUser]);
  
  const handleTelegramClick = () => {
    const telegramProvider = providers.find(p => p.type === 'telegram');
    if (!telegramProvider?.settings?.botUsername) return;
    
    // Use numeric botId if available, otherwise extract from env or show widget
    const botId = telegramProvider.settings.botId;
    
    if (!botId) {
      // Fallback: use Telegram Login Widget instead of OAuth popup
      // Create a temporary container for the widget
      const container = document.createElement('div');
      container.id = 'telegram-login-temp';
      container.style.display = 'none';
      document.body.appendChild(container);
      
      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', telegramProvider.settings.botUsername);
      script.setAttribute('data-size', 'large');
      script.setAttribute('data-radius', '8');
      script.setAttribute('data-onauth', 'onTelegramAuth(user)');
      script.setAttribute('data-request-access', 'write');
      container.appendChild(script);
      
      // Remove temp container after script loads
      script.onload = () => {
        setTimeout(() => container.remove(), 100);
      };
      
      return;
    }
    
    const width = 550;
    const height = 470;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    window.open(
      `https://oauth.telegram.org/auth?bot_id=${botId}&origin=${encodeURIComponent(window.location.origin)}&embed=1&request_access=write&return_to=${encodeURIComponent(window.location.href)}`,
      'telegram-login',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };
  
  const handleResendVerification = async () => {
    if (!email) return;
    
    setIsResending(true);
    setResendSuccess(false);
    
    try {
      await api.post('/auth/resend-verification', { email });
      setResendSuccess(true);
    } catch {
      // Ignore errors
    } finally {
      setIsResending(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendSuccess(false);
    setIsLoading(true);
    
    try {
      await login(email, password);
      navigate('/app');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      
      if (errorMessage.includes('verify your email') || errorMessage.includes('EmailNotVerified')) {
        setNeedsVerification(true);
        setError('Please verify your email. A verification link was sent to ' + email);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const emailProvider = providers.find(p => p.type === 'email');
  const telegramProvider = providers.find(p => p.type === 'telegram');
  const googleProvider = providers.find(p => p.type === 'google');
  const hasOAuthProviders = telegramProvider || googleProvider;
  
  return (
    <div className="w-full max-w-md">
      <div className="bg-neutral-0 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-sm p-8">
        <h2 className="text-xl font-medium text-neutral-900 dark:text-neutral-100 mb-6">
          Sign in to your account
        </h2>
        
        {isLoadingProviders ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
          </div>
        ) : (
          <>
            {error && (
              <div className={`px-4 py-3 rounded-md text-sm mb-4 ${
                needsVerification 
                  ? 'bg-warning-50 text-warning-700 border border-warning-500/20' 
                  : 'bg-error-50 text-error-600 border border-error-500/20'
              }`}>
                <div className="flex items-start gap-2">
                  {needsVerification ? (
                    <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p>{error}</p>
                    {needsVerification && (
                      <div className="mt-2">
                        {resendSuccess ? (
                          <p className="text-success-600 text-sm">✓ Email sent!</p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleResendVerification}
                            disabled={isResending}
                            className="inline-flex items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700"
                          >
                            <RefreshCw className={`w-3 h-3 ${isResending ? 'animate-spin' : ''}`} />
                            {isResending ? 'Sending...' : 'Resend verification email'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* OAuth Providers - Custom Buttons */}
            {hasOAuthProviders && (
              <div className="space-y-3 mb-6">
                {googleProvider && (
                  <button
                    type="button"
                    onClick={handleGoogleClick}
                    disabled={googleLoading || !googleScriptLoaded}
                    className="w-full py-2.5 px-4 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600 flex items-center justify-center gap-3 text-neutral-700 dark:text-neutral-200 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {googleLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <>
                        <GoogleIcon />
                        <span>Continue with Google</span>
                      </>
                    )}
                  </button>
                )}
                
                {telegramProvider && (
                  <button
                    type="button"
                    onClick={handleTelegramClick}
                    disabled={telegramLoading}
                    className="w-full py-2.5 px-4 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600 flex items-center justify-center gap-3 text-neutral-700 dark:text-neutral-200 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {telegramLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <>
                        <TelegramIcon />
                        <span>Continue with Telegram</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            
            {/* Divider */}
            {hasOAuthProviders && emailProvider && (
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200 dark:border-neutral-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-neutral-0 dark:bg-neutral-800 px-2 text-neutral-500">
                    or continue with email
                  </span>
                </div>
              </div>
            )}
            
            {/* Email Login Form */}
            {emailProvider && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder="you@example.com"
                    required
                    autoFocus={!hasOAuthProviders}
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input pr-10"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
            
            {!emailProvider && !hasOAuthProviders && (
              <div className="text-center py-4">
                <p className="text-neutral-500">No login methods are currently available.</p>
                <p className="text-sm text-neutral-400 mt-1">Please contact support.</p>
              </div>
            )}
          </>
        )}
        
        <div className="h-px bg-neutral-200 dark:bg-neutral-700 my-6" />
        
        <div className="text-sm text-center text-neutral-600 dark:text-neutral-400">
          Don't have an account?{' '}
          <Link to="/register" className="text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 font-medium">
            Create one
          </Link>
        </div>
      </div>
    </div>
  );
}
